import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import type { Question } from '@/types/form'
import { ValidationSettings } from './ValidationSettings'

interface NumberSettingsProps {
  question: Question
  onChange: (patch: Partial<Question['settings']>) => void
}

function numericValue(value: string) {
  if (value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function NumberSettings({ question, onChange }: NumberSettingsProps) {
  const isSlider = question.type === 'NUMBER_SLIDER'
  const minKey = isSlider ? 'sliderMin' : 'numberMin'
  const maxKey = isSlider ? 'sliderMax' : 'numberMax'
  const stepKey = isSlider ? 'sliderStep' : 'numberStep'
  const min = Number(question.settings[minKey] ?? 0)
  const max = Number(question.settings[maxKey] ?? (isSlider ? 100 : 1000000))
  const previewValue = Math.min(
    max,
    Math.max(min, Number(question.settings.validationTestValue ?? min)),
  )

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-950">
          {isSlider ? '滑桿數值設定' : '數字欄位設定'}
        </h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          設定填答者可輸入或拖曳的數值範圍，先以前端預覽方式保存。
        </p>
      </div>

      {!isSlider && (
        <div className="flex flex-col gap-2">
          <Label>Placeholder</Label>
          <Input
            value={question.settings.placeholder ?? ''}
            placeholder="請輸入數字"
            onChange={(event) => onChange({ placeholder: event.target.value })}
          />
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col gap-2">
          <Label>最小值</Label>
          <Input
            type="number"
            value={String(question.settings[minKey] ?? '')}
            onChange={(event) => onChange({ [minKey]: numericValue(event.target.value) })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>最大值</Label>
          <Input
            type="number"
            value={String(question.settings[maxKey] ?? '')}
            onChange={(event) => onChange({ [maxKey]: numericValue(event.target.value) })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>間距</Label>
          <Input
            type="number"
            min={0}
            value={String(question.settings[stepKey] ?? '')}
            onChange={(event) => onChange({ [stepKey]: numericValue(event.target.value) })}
          />
        </div>
      </div>

      {isSlider ? (
        <>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>顯示目前數值</span>
            <Switch
              checked={question.settings.sliderShowValue !== false}
              onCheckedChange={(sliderShowValue) => onChange({ sliderShowValue })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label>起點文案</Label>
              <Input
                value={question.settings.startLabel ?? ''}
                placeholder="低"
                onChange={(event) => onChange({ startLabel: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>終點文案</Label>
              <Input
                value={question.settings.endLabel ?? ''}
                placeholder="高"
                onChange={(event) => onChange({ endLabel: event.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>顯示刻度標籤</span>
            <Switch
              checked={question.settings.sliderShowTicks !== false}
              onCheckedChange={(sliderShowTicks) => onChange({ sliderShowTicks })}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>刻度標籤</Label>
            <Textarea
              className="min-h-24"
              value={(question.settings.sliderTickLabels ?? []).join('\n')}
              placeholder={'低\n中\n高'}
              onChange={(event) =>
                onChange({
                  sliderTickLabels: event.currentTarget.value
                    .split(/\r?\n/)
                    .map((item) => item.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{question.settings.startLabel ?? min}</span>
              <span>{question.settings.endLabel ?? max}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-indigo-100">
              <div className="h-full w-1/2 rounded-full bg-indigo-600" />
            </div>
            <div className="mt-2 text-xs text-slate-500">
              目前值預覽：{formatNumberPreview(previewValue, question)}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label>小數位</Label>
              <Input
                type="number"
                min={0}
                max={6}
                value={String(question.settings.numberDecimals ?? 0)}
                onChange={(event) => onChange({ numberDecimals: numericValue(event.target.value) })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>快速格式</Label>
              <Select
                value={(question.settings.validation as string | undefined) ?? 'number'}
                onValueChange={(validation) =>
                  onChange({ validation: validation as Question['settings']['validation'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="number">一般數字</SelectItem>
                  <SelectItem value="none">不限制</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-2">
              <Label>前綴</Label>
              <Input
                value={question.settings.numberPrefix ?? ''}
                placeholder="例如：TWD"
                onChange={(event) => onChange({ numberPrefix: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>單位</Label>
              <Input
                value={question.settings.numberUnit ?? ''}
                placeholder="例如：元、人、件"
                onChange={(event) => onChange({ numberUnit: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>後綴</Label>
              <Input
                value={question.settings.numberSuffix ?? ''}
                placeholder="例如：未稅"
                onChange={(event) => onChange({ numberSuffix: event.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span>顯示千分位</span>
            <Switch
              checked={Boolean(question.settings.numberUseThousands)}
              onCheckedChange={(numberUseThousands) => onChange({ numberUseThousands })}
            />
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="text-xs font-medium text-slate-500">顯示預覽</div>
            <div className="mt-1 font-semibold text-slate-950">
              {formatNumberPreview(123456.789, question)}
            </div>
          </div>
          <ValidationSettings question={question} onChange={onChange} />
        </>
      )}
    </div>
  )
}

function formatNumberPreview(value: number, question: Question) {
  const decimals = Number(question.settings.numberDecimals ?? 0)
  const formatted = question.settings.numberUseThousands
    ? new Intl.NumberFormat('zh-TW', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    : value.toFixed(decimals)
  return [
    question.settings.numberPrefix,
    formatted,
    question.settings.numberUnit,
    question.settings.numberSuffix,
  ]
    .filter(Boolean)
    .join(' ')
}
